/**
 * @file 資料どうしの対応づけ (問 ↔ 別の資料のページ) の突き合わせ (純関数)。
 *
 * @what: 資料 S の問の index[field] の値を、相手の資料 T の目次の見出しと索引の語に
 *   突き合わせる。問 → T のページ (forwardLinks) と、T のページ → その語を持つ S の問
 *   (reverseItems) の両向き。語の比較は正規化 (全角半角・空白・記号・ダッシュを落とす) の
 *   完全一致か、5 文字以上の包含。
 * @why: 過去問とテキスト、体系書と演習書のように、資料どうしの往復は見出し・語の一致で
 *   引ける (別冊の解答例の目次一致と同じ仕組み)。OCR 由来の表記ゆれ (改行・「－」と「一」)
 *   を吸収するために正規化する。3 級テキストは目次に遺産名を持たないので索引が要る。
 * @failure: 例外を投げない。一致しなければ空。
 */

import type { StudyScanItem } from "../api/scans";
import type { StudySourceIndexEntry } from "../api/sourceIndex";
import type { StudySource, StudySourceTocEntry } from "../api/sources";
import { tocEntriesAt } from "./sourcePages";

/**
 * 包含で一致とみなす最短の長さ (正規化後の文字数)。
 *
 * @why: OCR で折り返しの続きが欠けた遺産名 (「ウェストミンスター宮殿」と「ウェストミンスター
 *   宮殿、ウェストミンスター・アビーと…」) を結ぶために包含を許すが、短い語まで許すと
 *   「知床」が「知床半島」や「知床五湖」に、「屋久島」が「屋久杉」の見出しに結びつく。
 *   3 級テキストの索引 339 語で、4 文字以下の語 (法隆寺・姫路城・知床) は完全一致で
 *   引けることを確かめたうえで 5 に置いた (2026-09-03)。語彙が変われば見直す。
 */
const CONTAINS_MIN = 5;

/** 突き合わせ用の正規化。NFKC → 空白・記号・ダッシュ類 (OCR で「一」になるものを含む) を落とす。 */
export function normalizeTerm(s: string): string {
  return s.normalize("NFKC").replace(/[\s\-‐‑–—―−ー一・:：、,，.。「」『』（）()［］[\]…•·]/g, "");
}

/** 正規化した 2 語が同じか、片方がもう片方を含む (両方 5 文字以上)。 */
export function termsMatch(a: string, b: string): boolean {
  const x = normalizeTerm(a);
  const y = normalizeTerm(b);
  if (x === "" || y === "") return false;
  if (x === y) return true;
  if (x.length < CONTAINS_MIN || y.length < CONTAINS_MIN) return false;
  return x.includes(y) || y.includes(x);
}

/**
 * 問の index[field] の値。JSON の配列 (取り込みが配列を文字列にしたもの) は要素に分け、
 * それ以外は 1 つの値。空は無し。
 */
export function fieldValues(index: Record<string, string>, field: string): string[] {
  const raw = index[field]?.trim() ?? "";
  if (raw === "") return [];
  if (raw.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((v): v is string => typeof v === "string" && v.trim() !== "");
      }
    } catch {
      // 配列でなければ 1 つの値として扱う
    }
  }
  return [raw];
}

/** 相手の資料 (目次は資料が持ち、索引は別に取る)。 */
export interface LinkTarget {
  source: StudySource;
  index: readonly StudySourceIndexEntry[];
}

/** 問から引いた相手のページ 1 つ。 */
export interface ForwardLink {
  source: StudySource;
  page: number;
  /** 一致した見出しか語。 */
  label: string;
}

/**
 * 問 → 相手の資料のページ。
 *
 * @what: 資料の対応づけ (related) ごとに、問の index[field] の値を相手の目次の見出しと
 *   索引の語に突き合わせ、一致したページを集める。同じ資料の同じページは 1 つにまとめ、
 *   続きのページ (14, 15, 16) は先頭だけ残す (演習書の節をページごとに引いた索引を
 *   1 行にするため)。並びは資料の対応づけの順、ページ順。
 */
export function forwardLinks(
  item: StudyScanItem,
  source: StudySource,
  targets: ReadonlyMap<string, LinkTarget>,
): ForwardLink[] {
  // 相手の資料ごとに (対応づけの順)、その資料を指す全ての対応づけの一致を集める。
  const byTarget = new Map<string, { page: number; label: string }[]>();
  for (const rel of source.related ?? []) {
    const target = targets.get(rel.source_id);
    if (!target) continue;
    const hits = byTarget.get(rel.source_id) ?? [];
    byTarget.set(rel.source_id, hits);
    for (const v of fieldValues(item.index, rel.field)) {
      for (const e of target.source.toc ?? []) {
        if (termsMatch(v, e.title)) hits.push({ page: e.page, label: e.title });
      }
      for (const e of target.index) {
        if (termsMatch(v, e.term)) hits.push({ page: e.page, label: e.term });
      }
    }
  }
  const out: ForwardLink[] = [];
  for (const [id, hits] of byTarget) {
    const target = targets.get(id);
    if (!target || hits.length === 0) continue;
    // ページ順 (同じページは先の対応づけの一致が先)。同じページは 1 つ、続きのページは先頭だけ。
    hits.sort((a, b) => a.page - b.page);
    let prevPage = Number.NEGATIVE_INFINITY;
    for (const h of hits) {
      if (h.page === prevPage) continue;
      const consecutive = h.page === prevPage + 1;
      prevPage = h.page;
      if (consecutive) continue;
      out.push({ source: target.source, page: h.page, label: h.label });
    }
  }
  return out;
}

/** 相手のページに関する問 (資料ごと)。 */
export interface ReverseGroup {
  source: StudySource;
  items: StudyScanItem[];
}

/** そのページの語 (目次の見出しと、索引でそのページを指す語)。 */
export function termsOnPage(
  toc: readonly StudySourceTocEntry[] | undefined,
  index: readonly StudySourceIndexEntry[],
  page: number,
): string[] {
  const terms = tocEntriesAt(toc, page).map((e) => e.title);
  for (const e of index) if (e.page === page) terms.push(e.term);
  return terms;
}

/**
 * 相手のページ → その語を持つ問。
 *
 * @what: 自分 (T) を相手に指定している資料 S ごとに、T のこのページの語 (目次の見出しと
 *   索引の語) に index[field] が一致する S の問を集める (S の問の並びのまま)。
 *   問が 1 つも無い資料は出さない。
 */
export function reverseItems(
  target: StudySource,
  targetIndex: readonly StudySourceIndexEntry[],
  page: number,
  referrers: readonly { source: StudySource; items: readonly StudyScanItem[] }[],
): ReverseGroup[] {
  const terms = termsOnPage(target.toc, targetIndex, page);
  if (terms.length === 0) return [];
  const out: ReverseGroup[] = [];
  for (const ref of referrers) {
    const fields = (ref.source.related ?? [])
      .filter((r) => r.source_id === target.id)
      .map((r) => r.field);
    if (fields.length === 0) continue;
    const items = ref.items.filter((it) =>
      fields.some((f) => fieldValues(it.index, f).some((v) => terms.some((t) => termsMatch(v, t)))),
    );
    if (items.length > 0) out.push({ source: ref.source, items });
  }
  return out;
}

/** 対応づけで sourceId を相手に指定している資料。 */
export function referrersOf(sources: readonly StudySource[], sourceId: string): StudySource[] {
  return sources.filter((s) => s.related?.some((r) => r.source_id === sourceId));
}
